import React, { useEffect, useRef, useState } from "react";
import { X, Eraser, Check } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";

// Pavé de signature tactile/souris. onSave(dataUrl, signerName).
export const SignaturePad = ({ open, onClose, onSave, defaultName = "", saving = false }) => {
  const { t } = useLang();
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const [name, setName] = useState(defaultName);

  useEffect(() => { if (open) setName(defaultName); }, [open, defaultName]);

  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
    hasInk.current = false;
  }, [open]);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const { x, y } = pos(e); ctx.lineTo(x, y); ctx.stroke(); hasInk.current = true; };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasInk.current = false;
  };

  const save = () => {
    if (!hasInk.current) return;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onSave(dataUrl, name);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="signature-modal">
      <div className="w-full max-w-lg border border-border bg-card p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-xl font-black tracking-tight text-foreground">{t.clientSignature}</h2>
          <button data-testid="signature-close" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={20} weight="bold" /></button>
        </div>
        <label className="mb-3 block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t.signerName}</span>
          <input data-testid="signer-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        </label>
        <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t.signHere}</p>
        <div className="relative border-2 border-dashed border-border bg-white" style={{ touchAction: "none" }}>
          <canvas
            ref={canvasRef}
            data-testid="signature-canvas"
            className="block h-48 w-full cursor-crosshair"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          />
        </div>
        <div className="mt-4 flex items-center justify-between">
          <button data-testid="signature-clear" onClick={clear} className="inline-flex items-center gap-2 border border-border px-3 py-2 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-muted transition-colors duration-200">
            <Eraser size={14} weight="bold" /> {t.clear}
          </button>
          <div className="flex gap-2">
            <button data-testid="signature-skip" onClick={onClose} className="border border-border px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:bg-muted transition-colors duration-200">{t.later}</button>
            <button data-testid="signature-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-primary px-4 py-2 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
              <Check size={14} weight="bold" /> {t.saveSignature}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
